import React from 'react';
import styled from 'styled-components';
import { Colors } from '../../constants/theme';

interface IconProps {
  size?: number;
  color?: string;
  className?: string;
}

const IconWrapper = styled.svg<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  fill: currentColor;
  flex-shrink: 0;
`;

// Refresh Icon - Only for refresh actions
export const RefreshIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C9.61061 4 7.46589 5.11509 6.07065 6.86447M4 4V8H8" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Live Status Indicator - Only for live status
export const LiveIndicator: React.FC<{ active?: boolean }> = ({ active = true }) => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="4" cy="4" r="4" fill={active ? Colors.success : Colors.warning}>
      {active && (
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      )}
    </circle>
  </svg>
);

// Dashboard Icon
export const DashboardIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="7" height="7" stroke={color} strokeWidth="2" />
    <rect x="14" y="3" width="7" height="7" stroke={color} strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" stroke={color} strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" stroke={color} strokeWidth="2" />
  </IconWrapper>
);

// Analytics Icon
export const AnalyticsIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M3 12L7 8L11 10L15 6L21 12" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <rect x="3" y="17" width="4" height="4" fill={color} />
    <rect x="10" y="15" width="4" height="6" fill={color} />
    <rect x="17" y="13" width="4" height="8" fill={color} />
  </IconWrapper>
);

// People Icon
export const PeopleIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="7" r="3" stroke={color} strokeWidth="2" />
    <circle cx="15" cy="7" r="3" stroke={color} strokeWidth="2" />
    <path 
      d="M3 19C3 16.2386 5.23858 14 8 14H10M21 19C21 16.2386 18.7614 14 16 14H14" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round"
    />
  </IconWrapper>
);

// Document Icon
export const DocumentIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M7 3H17C18.1046 3 19 3.89543 19 5V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3Z" 
      stroke={color} 
      strokeWidth="2"
    />
    <path d="M9 7H15" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M9 11H15" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M9 15H12" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </IconWrapper>
);

// Settings Icon
export const SettingsIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="2" />
    <path 
      d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round"
    />
  </IconWrapper>
);

// Add Icon
export const AddIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M12 5V19M5 12H19" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Checkmark Icon
export const CheckIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M5 12L10 17L20 7" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Arrow Right Icon
export const ArrowRightIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M5 12H19M19 12L12 5M19 12L12 19" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Info Icon
export const InfoIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <path d="M12 8V8.01" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 12V16" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </IconWrapper>
);

// Warning Icon
export const WarningIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M12 2L2 20H22L12 2Z" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path d="M12 9V13" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 17V17.01" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </IconWrapper>
);

// Export Icon
export const ExportIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M12 3V15M12 3L8 7M12 3L16 7" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M3 15V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V15" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round"
    />
  </IconWrapper>
);

// Filter Icon
export const FilterIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M3 4H21L14 12V18L10 20V12L3 4Z" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Calendar Icon
export const CalendarIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="2" />
    <path d="M3 9H21" stroke={color} strokeWidth="2" />
    <path d="M8 2V6" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M16 2V6" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </IconWrapper>
);

// Clock Icon
export const ClockIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <path d="M12 6V12L16 14" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </IconWrapper>
);

// Chevron Down Icon
export const ChevronDownIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Arrow Up Icon
export const ArrowUpIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 19V5M5 12L12 5L19 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Arrow Down Icon
export const ArrowDownIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5V19M19 12L12 19L5 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Chevron Up Icon
export const ChevronUpIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Close Icon
export const CloseIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => (
  <IconWrapper size={size} className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

export default {
  RefreshIcon,
  LiveIndicator,
  DashboardIcon,
  AnalyticsIcon,
  PeopleIcon,
  DocumentIcon,
  SettingsIcon,
  AddIcon,
  CheckIcon,
  ArrowRightIcon,
  InfoIcon,
  WarningIcon,
  ExportIcon,
  FilterIcon,
  CalendarIcon,
  ClockIcon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronUpIcon,
  CloseIcon
};